// lab 페이지(버핏스코어·유사기업·리포트·부동산)에서 생성한 보고서 기록소.
//
// 문서함의 진실 소스는 대화 스냅샷이지만 lab 파일은 대화 밖에서 생기므로 따로 기록하고
// buildLibraryIndex 가 병합한다. 기록은 **서버(/agent/user-docs/labfiles, 계정별)** 가 진실 소스 —
// 다른 PC·모바일에서도 같은 목록이 보인다. localStorage(사용자별 키)는 즉시 렌더용 캐시이자
// 서버 저장소가 없을 때의 폴백이며, 남아 있던 옛 기록은 첫 동기화 때 서버로 옮긴다.
import { api, authHeaders } from "./api";
import { useAuth } from "../stores/auth";

export interface LabFile {
  url: string;       // S3 등 원본 URL
  label: string;     // 파일명
  kind: string;      // report | excel
  feature: string;   // 버핏스코어 | 유사기업 | 산업분류
  page: string;      // 원본 페이지 경로 (/buffett, /peer)
  company: string;
  created: string;   // ISO
}

const LIMIT = 200;

function storeKey(): string {
  // 사용자 구분은 채팅 스냅샷과 같은 규칙 (uid||username||email 소문자, 없으면 _anon)
  try {
    const u = useAuth.getState().user as Record<string, unknown> | null;
    const k = String(u?.uid || u?.username || u?.email || "_anon").toLowerCase();
    return `va_lab_files::${k}`;
  } catch {
    return "va_lab_files::_anon";
  }
}

export function labFiles(): LabFile[] {
  try {
    return JSON.parse(localStorage.getItem(storeKey()) || "[]") as LabFile[];
  } catch {
    return [];
  }
}

function writeLocal(list: LabFile[]): void {
  try { localStorage.setItem(storeKey(), JSON.stringify(list.slice(0, LIMIT))); } catch { /* 캐시 실패는 무시 */ }
}

/** 서버 문서 ID — URL 에서 결정적으로 만든다(같은 파일을 다시 기록하면 덮어쓴다). 소문자 영숫자 6~40자 */
export function labDocId(url: string): string {
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < url.length; i += 1) {
    h1 = Math.imul(h1 ^ url.charCodeAt(i), 16777619) >>> 0;
    h2 = Math.imul(h2 + url.charCodeAt(i), 2246822519) >>> 0;
  }
  return `f${h1.toString(36)}${h2.toString(36)}`.padEnd(6, "0").slice(0, 40);
}

type Wrapped<T> = { ok: boolean; data?: T; message?: string };
type ServerDoc = { id: string; payload: Partial<LabFile> | null; created: string };

async function serverList(): Promise<LabFile[] | null> {
  if (!useAuth.getState().user) return null;
  try {
    const d = await api<Wrapped<ServerDoc[]>>("/agent/user-docs/labfiles", { headers: authHeaders() });
    if (!d.ok || !Array.isArray(d.data)) return null;
    return d.data.flatMap((doc) => {
      const p = doc.payload;
      if (!p || typeof p.url !== "string" || typeof p.label !== "string") return [];
      return [{ url: p.url, label: p.label, kind: p.kind || "report", feature: p.feature || "", page: p.page || "", company: p.company || "", created: p.created || doc.created }];
    });
  } catch {
    return null;    // 서버 저장소 없음·네트워크 실패 → 로컬 캐시로
  }
}

async function serverPut(f: LabFile): Promise<boolean> {
  try {
    const d = await api<Wrapped<unknown>>(`/agent/user-docs/labfiles/${labDocId(f.url)}`, { method: "PUT", body: JSON.stringify({ name: f.label, address: f.company, summary: f.feature, payload: f }) });
    return Boolean(d.ok);
  } catch {
    return false;
  }
}

/** 기록 — 로컬 캐시에 바로 넣고(문서함 즉시 반영) 서버에도 올린다. 실패해도 생성 흐름을 막지 않는다 */
export function recordLabFile(f: Omit<LabFile, "created">): void {
  const entry: LabFile = { ...f, created: new Date().toISOString() };
  writeLocal([entry, ...labFiles().filter((x) => x.url !== f.url)]);
  void serverPut(entry);
}

let syncing: Promise<LabFile[]> | null = null;

/** 서버 목록으로 캐시를 맞춘다(문서함이 열릴 때). 로컬에만 있던 항목은 서버로 옮긴다. 서버가 없으면 로컬 그대로 */
export function syncLabFiles(): Promise<LabFile[]> {
  if (syncing) return syncing;
  syncing = (async () => {
    const local = labFiles();
    const remote = await serverList();
    if (remote === null) return local;
    const remoteUrls = new Set(remote.map((f) => f.url));
    const onlyLocal = local.filter((f) => !remoteUrls.has(f.url));
    const uploaded: LabFile[] = [];
    for (const f of onlyLocal) if (await serverPut(f)) uploaded.push(f);
    const merged = [...remote, ...uploaded].sort((a, b) => Date.parse(b.created) - Date.parse(a.created));
    // 서버에 못 올린 로컬 항목은 캐시에 남겨 다음에 다시 시도
    const leftover = onlyLocal.filter((f) => !uploaded.includes(f));
    writeLocal([...merged, ...leftover]);
    return [...merged, ...leftover];
  })().finally(() => { syncing = null; });
  return syncing;
}
